// src/finances/AuditTrail.js
// The whole financial diary — superadmin only. Every create/update/delete on
// invoices, quotes, payments, and informal estimates, newest first, with
// filters by user, record type, and action. Entries are append-only; there is
// no way to edit or remove them, by design.
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Clock, Search } from 'lucide-react';
import { API_BASE } from '../apiBase';
import { fmtAuditVal } from '../lib/AuditDrawer';

const ACTION_TONE = {
  create: 'bg-emerald-500/15 text-emerald-300',
  update: 'bg-indigo-500/15 text-indigo-300',
  delete: 'bg-red-500/15 text-red-300',
};
const TYPE_LABEL = { Invoice: 'Invoice', Quote: 'Quote', Payment: 'Payment', InformalEstimate: 'Informal Estimate' };

function AuditTrail() {
  const [rows, setRows] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API_BASE}/audit?limit=500`, { headers: { Authorization: localStorage.getItem('token') } })
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]));
  }, []);

  const users = useMemo(() => {
    const seen = new Map();
    (rows || []).forEach(r => {
      const id = r.actor?._id;
      if (id && !seen.has(id)) seen.set(id, r.actor.name || r.actor.email || 'Unknown');
    });
    return [...seen.entries()]; // [id, name]
  }, [rows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows || []).filter(r =>
      (!typeFilter || r.entityType === typeFilter) &&
      (!actionFilter || r.action === actionFilter) &&
      (!userFilter || r.actor?._id === userFilter) &&
      (!q || [r.entityLabel, r.project?.poNumber, r.project?.title, r.actor?.name]
        .some(v => String(v || '').toLowerCase().includes(q)))
    );
  }, [rows, typeFilter, actionFilter, userFilter, search]);

  const sel = "bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-zinc-200 outline-none focus:border-indigo-600/60";

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-6 max-md:p-3 space-y-5">
      <div>
        <h1 className="text-3xl max-md:text-xl font-bold tracking-tight flex items-center gap-3">
          <Clock className="text-indigo-500" /> Audit Trail
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Every financial action — who, what, when, before → after. Append-only: entries can't be edited or deleted.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search number, PO, user…"
            className={`${sel} pl-9 w-56 max-md:w-full`}
          />
        </div>
        <select className={sel} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All records</option>
          {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}s</option>)}
        </select>
        <select className={sel} value={actionFilter} onChange={e => setActionFilter(e.target.value)}>
          <option value="">All actions</option>
          <option value="create">Created</option>
          <option value="update">Updated</option>
          <option value="delete">Deleted</option>
        </select>
        <select className={sel} value={userFilter} onChange={e => setUserFilter(e.target.value)}>
          <option value="">All users</option>
          {users.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        {rows !== null && <span className="text-xs text-zinc-600 ml-1">{visible.length} entries</span>}
      </div>

      {/* Ledger — desktop table */}
      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl max-md:hidden">
        <table className="min-w-full divide-y divide-gray-800/60">
          <thead className="bg-gray-950/30">
            <tr>
              <th className="px-5 py-3.5 text-left text-xs font-semibold text-zinc-400 uppercase">When</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold text-zinc-400 uppercase">Who</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold text-zinc-400 uppercase">Action</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold text-zinc-400 uppercase">Record</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold text-zinc-400 uppercase">Project</th>
              <th className="px-5 py-3.5 text-left text-xs font-semibold text-zinc-400 uppercase">Changes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/40">
            {rows === null && <tr><td colSpan={6} className="px-6 py-10 text-center text-zinc-600">Loading…</td></tr>}
            {rows !== null && visible.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-10 text-center text-zinc-600">No entries{rows.length ? ' match the filters' : ' yet — the diary fills as financial actions happen'}.</td></tr>
            )}
            {visible.map(r => (
              <tr key={r._id} className="hover:bg-gray-800/40">
                <td className="px-5 py-3 whitespace-nowrap text-xs text-zinc-400">{new Date(r.at).toLocaleString()}</td>
                <td className="px-5 py-3 whitespace-nowrap text-sm text-zinc-200 font-medium">
                  {r.actor?.name || r.actor?.email || 'Unknown'}
                  {r.actorRole && <span className="text-zinc-600 text-xs"> · {r.actorRole}</span>}
                </td>
                <td className="px-5 py-3 whitespace-nowrap">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${ACTION_TONE[r.action] || 'bg-zinc-700/40 text-zinc-300'}`}>{r.action}</span>
                </td>
                <td className="px-5 py-3 whitespace-nowrap text-sm">
                  <span className="text-zinc-500 text-xs">{TYPE_LABEL[r.entityType] || r.entityType}</span>{' '}
                  <span className="text-zinc-200">{r.entityLabel || '—'}</span>
                </td>
                <td className="px-5 py-3 whitespace-nowrap text-xs text-zinc-400">
                  {r.project ? `PO ${r.project.poNumber || ''}${r.project.title ? ` — ${r.project.title}` : ''}` : '—'}
                </td>
                <td className="px-5 py-3 text-xs text-zinc-300">
                  {(r.changes || []).map((c, i) => (
                    <span key={i} className="block">
                      <span className="text-zinc-500">{c.field}:</span>{' '}
                      {r.action === 'create'
                        ? fmtAuditVal(c.field, c.to)
                        : <>{fmtAuditVal(c.field, c.from)} <span className="text-zinc-600">→</span> {fmtAuditVal(c.field, c.to)}</>}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ledger — mobile cards */}
      <div className="md:hidden space-y-2">
        {rows === null && <p className="text-zinc-600 text-sm text-center py-8">Loading…</p>}
        {rows !== null && visible.length === 0 && <p className="text-zinc-600 text-sm text-center py-8">No entries.</p>}
        {visible.map(r => (
          <div key={r._id} className="bg-gray-900 border border-gray-800/60 rounded-xl p-3">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${ACTION_TONE[r.action] || 'bg-zinc-700/40 text-zinc-300'}`}>{r.action}</span>
              <span className="text-[10px] text-zinc-500">{new Date(r.at).toLocaleString()}</span>
            </div>
            <p className="text-sm text-zinc-200 font-semibold">{r.actor?.name || r.actor?.email || 'Unknown'} <span className="text-zinc-500 font-normal">· {TYPE_LABEL[r.entityType] || r.entityType} {r.entityLabel}</span></p>
            {r.project && <p className="text-[11px] text-zinc-500">PO {r.project.poNumber} {r.project.title ? `— ${r.project.title}` : ''}</p>}
            {(r.changes || []).map((c, i) => (
              <p key={i} className="text-xs text-zinc-300 mt-0.5">
                <span className="text-zinc-500">{c.field}:</span>{' '}
                {r.action === 'create' ? fmtAuditVal(c.field, c.to) : <>{fmtAuditVal(c.field, c.from)} → {fmtAuditVal(c.field, c.to)}</>}
              </p>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default AuditTrail;
